import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Toggles the cart drawer upsell product on and off without leaving the drawer, and adds
 * the empty bag's best sellers (snippets/cart-drawer-empty.liquid) from their cards.
 *
 * The form carries data-upsell-action: 'add' posts to /cart/add, 'remove' posts a
 * quantity-0 change for the line key. Either way it dispatches a CartLinesUpdateEvent
 * carrying the section HTML, so cart-items-component morphs the drawer (items, totals,
 * progress bar and this upsell) the same way it does for product-form.js. The event is
 * only sent once the request succeeded: a failure leaves the cart untouched, and skipping
 * the re-render keeps the error visible. Without JS the form still posts on its own.
 */
class CartUpsellComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('change', this.#onChange);
  }

  /**
   * Reacts to an empty-bag card's option pickers. A dropdown hides its native select behind
   * a [data-variant-label], so the choice is written back to that label first; then the
   * card's pickers are resolved to a variant.
   * @param {Event} event
   */
  #onChange = (event) => {
    const field = event.target;

    if (field instanceof HTMLSelectElement) {
      const label = field.parentElement?.querySelector('[data-variant-label]');
      if (label) label.textContent = field.selectedOptions[0]?.textContent?.trim() ?? '';
    }

    this.#syncVariant();
  };

  /**
   * Resolves the option pickers to one variant and reflects it in the card: the id that
   * gets posted, the price, the thumbnail and whether it can be added at all. Forms with
   * no [data-variants] payload (single-variant cards, the filled drawer's upsell) have
   * nothing to resolve.
   */
  #syncVariant() {
    const payload = this.querySelector('script[data-variants]');
    const idInput = this.querySelector('input[name="id"]');
    if (!payload || !(idInput instanceof HTMLInputElement)) return;

    /** @type {(string | undefined)[]} */
    const selected = [];

    for (const field of this.querySelectorAll('[data-option-position]')) {
      if (field instanceof HTMLInputElement && !field.checked) continue;
      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) continue;

      selected[Number(field.dataset.optionPosition) - 1] = field.value;
    }

    // A dropdown still on its placeholder. Its `required` blocks the submit, so the card is left as it is.
    if (selected.includes('')) return;

    /** @type {{ id: number, available: boolean, options: string[], price: string, compare: string | null, image: string | null }[]} */
    const variants = JSON.parse(payload.textContent ?? '[]');
    const variant = variants.find((candidate) =>
      candidate.options.every((value, index) => selected[index] === undefined || selected[index] === value)
    );

    const button = this.querySelector('button[type="submit"]');

    if (button instanceof HTMLButtonElement) {
      const available = Boolean(variant?.available);
      const label = available ? button.dataset.labelAdd : button.dataset.labelSoldOut;

      button.disabled = !available;
      if (label) button.textContent = label;
    }

    if (!variant) return;

    idInput.value = String(variant.id);

    const price = this.querySelector('[data-price]');
    if (price) price.textContent = variant.price;

    const compare = this.querySelector('[data-compare-price]');
    if (compare instanceof HTMLElement) {
      compare.textContent = variant.compare ?? '';
      compare.hidden = !variant.compare;
    }

    const image = this.querySelector('[data-variant-image]');
    if (image instanceof HTMLImageElement && variant.image) {
      // The srcset was built for the first variant's photo and would win over a new src.
      image.removeAttribute('srcset');
      image.src = variant.image;
    }
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');
    this.#setError('');

    const isRemove = form.dataset.upsellAction === 'remove';
    const formData = new FormData(form);

    // The option radios only exist to pick the variant; /cart/add takes the resolved id.
    for (const name of [...formData.keys()]) {
      if (name.startsWith('cart-empty-option-')) formData.delete(name);
    }
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
      .filter(Boolean);
    const lineId = String(formData.get('id'));

    try {
      if (isRemove) {
        const body = JSON.stringify({
          id: lineId,
          quantity: 0,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        });
        const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
        const result = await response.json();

        if (result.errors || result.status) {
          const message = result.errors || result.description || result.message || 'Could not update cart';

          this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
          this.#setError(message);
          return;
        }

        const deferred = CartLinesUpdateEvent.createPromise();

        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'remove',
            context: 'cart',
            lines: [{ id: lineId, quantity: 0 }],
            promise: deferred.promise,
          })
        );

        // /cart/change returns the whole cart, so there is no follow-up /cart.js fetch here.
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
          detail: {
            items: result.items,
            source: 'cart-upsell-component',
            itemCount: result.item_count,
            sections: result.sections,
            didError: false,
          },
        });
        return;
      }

      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body: formData }));
      const result = await response.json();

      if (result.status) {
        const message = result.description || result.message || 'Could not add to cart';

        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        this.#setError(message);
        return;
      }

      const ajaxCart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
        (cartResponse) => cartResponse.json()
      );
      const deferred = CartLinesUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: lineId, quantity: 1 }],
          promise: deferred.promise,
        })
      );

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'cart-upsell-component',
          itemCount: 1,
          sections: result.sections,
          didError: false,
        },
      });
    } catch (error) {
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during cart update',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#setError(isRemove ? 'Could not update cart. Please try again.' : 'Could not add to cart. Please try again.');
    } finally {
      button?.removeAttribute('aria-busy');
    }
  };

  /** @param {string} message */
  #setError(message) {
    const error = this.querySelector('.cart-upsell__error');
    if (!(error instanceof HTMLElement)) return;

    error.textContent = message;
    error.hidden = !message;
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
